/*
 * Copyright 2026 Bytefinger Technology (HK) Co., Limited (https://bytefinger.ai)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { axiosInstance } from './axios';

export enum FileIndexStatus {
  Pending = 0,
  Indexing = 1,
  Done = 2,
  Failed = 3,
}

export interface FileMemoryFile {
  id: string;
  conversation_id: string;
  file_key: string;
  file_name: string;
  file_type: string;
  file_size: number;
  page_count: number;
  sheet_count: number;
  slide_count: number;
  index_status: FileIndexStatus;
  retry_count: number;
  chunk_count: number;
  chunking_strategy: string;
  error_msg: string;
  file_process_backend: 'conversational_file_memory' | 'google_file_search';
  external_store_name: string;
  external_document_name: string;
  external_operation_name: string;
  created_at: number;
  updated_at: number;
}

interface ListFilesResponse {
  data: FileMemoryFile[];
  code: number;
  msg: string;
}

interface FileStatusResponse {
  data: FileMemoryFile;
  code: number;
  msg: string;
}

interface ActionResponse {
  code: number;
  msg: string;
}

interface FileMemoryConfigResponse {
  data: { google_file_search_enabled: boolean };
  code: number;
  msg: string;
}

export const fileMemoryApi = {
  getConfig: async (): Promise<{ google_file_search_enabled: boolean }> => {
    const res = await axiosInstance.request<unknown, FileMemoryConfigResponse>({
      url: '/api/filememory/config',
      method: 'GET',
    });
    return res.data ?? { google_file_search_enabled: false };
  },

  listFiles: async (
    conversationId: string,
    signal?: AbortSignal,
  ): Promise<FileMemoryFile[]> => {
    const res = await axiosInstance.request<unknown, ListFilesResponse>({
      url: '/api/filememory/files',
      method: 'GET',
      params: { conversation_id: conversationId },
      signal,
    });
    return res.data ?? [];
  },

  getFileStatus: async (
    fileId: string,
    signal?: AbortSignal,
  ): Promise<FileMemoryFile> => {
    const res = await axiosInstance.request<unknown, FileStatusResponse>({
      url: `/api/filememory/files/${fileId}/status`,
      method: 'GET',
      signal,
    });
    return res.data;
  },

  deleteFile: async (fileId: string): Promise<void> => {
    await axiosInstance.request<ActionResponse>({
      url: `/api/filememory/files/${fileId}`,
      method: 'DELETE',
    });
  },

  retryFile: async (fileId: string): Promise<void> => {
    await axiosInstance.request<ActionResponse>({
      url: `/api/filememory/files/${fileId}/retry`,
      method: 'POST',
    });
  },
};
